/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description Show worklists button
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import clsx from 'clsx';
import { Tooltip, Typography, List, Card, Spin } from 'antd';

import Button from 'components/widgets/button';
import useGetEntityWorklists from 'hooks/claim/mutate/useGetEntityWorklists';
import { WORKLIST_ENTITY_TYPES } from 'app-constants/claims/workLists';

const PAGE_SIZE = 10;

/**
 * IProps
 */
export interface IProps {
	showUnderline?: boolean;
	isLoadingWorklists?: boolean;
	buttonStyle?: React.CSSProperties;
	className?: string;
	entityId?: string;
	totalCount: number;
	initialEntityWorklists?: ClaimWorkLists.IEntityWorklistResponse[];
}

/**
 * ShowWorklists
 */
const ShowWorklists: React.FC<IProps> = ({
	showUnderline,
	buttonStyle,
	className = 'justify-start',
	isLoadingWorklists,
	entityId,
	totalCount,
	initialEntityWorklists,
}) => {
	const total = totalCount;
	const isPaginated = !!entityId && !_.isEmpty(initialEntityWorklists) && total > (initialEntityWorklists?.length ?? 0);

	const [paginatedWorklists, setPaginatedWorklists] = useState<ClaimWorkLists.IEntityWorklistResponse[]>([]);
	const currentPageRef = useRef(1);
	const hasMoreRef = useRef(true);

	const { mutate, isPending } = useGetEntityWorklists();

	const allLoadedWorklists = useMemo(
		() => [...(initialEntityWorklists ?? []), ...paginatedWorklists],
		[initialEntityWorklists, paginatedWorklists],
	);

	const handleScroll = useCallback(
		(e: React.UIEvent<HTMLDivElement>) => {
			if (!isPaginated || !hasMoreRef?.current || isPending) {
				return;
			}
			const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
			if (scrollHeight - scrollTop - clientHeight < 50) {
				const nextPage = currentPageRef.current + 1;
				mutate(
					{
						entityType: WORKLIST_ENTITY_TYPES.claim as ClaimWorkLists.IWorkListEntityType,
						entityId,
						pgNum: nextPage,
						pgSize: PAGE_SIZE,
					},
					{
						onSuccess: (data) => {
							setPaginatedWorklists((prev) => [...prev, ...(data?.data || [])]);
							currentPageRef.current = data?.currentPage;
							hasMoreRef.current = !!data?.nextPage;
						},
					},
				);
			}
		},
		[isPaginated, isPending, entityId, mutate],
	);

	const handleOpenChange = useCallback(
		(open: boolean) => {
			if (!open && isPaginated) {
				setPaginatedWorklists([]);
				currentPageRef.current = 1;
				hasMoreRef.current = true;
			}
		},
		[isPaginated],
	);

	if (isLoadingWorklists) {
		return <Spin />;
	}
	if (total > 0) {
		return (
			<Tooltip
				arrow={false}
				color='#fff'
				classNames={{ root: 'enrollment-tooltip', body: 'w-[350px] p-0' }}
				onOpenChange={handleOpenChange}
				title={
					<Card
						size='small'
						title={
							<Typography.Title level={5} className='m-0'>
								{`Worklist Details (${total})`}
							</Typography.Title>
						}
						classNames={{ body: 'p-0' }}
					>
						{isPaginated ? (
							<div className='max-h-[40vh] overflow-auto' onScroll={handleScroll}>
								<List
									size='small'
									dataSource={allLoadedWorklists}
									renderItem={(worklist) => (
										<List.Item>
											<Button.ViewClaimWorklistDetails worklist={worklist} />
										</List.Item>
									)}
								/>
								{isPending && (
									<div className='flex justify-center py-2'>
										<Spin size='small' />
									</div>
								)}
							</div>
						) : (
							<List
								size='small'
								className='max-h-[50vh] overflow-auto'
								dataSource={initialEntityWorklists}
								renderItem={(worklist) => (
									<List.Item>
										<Button.ViewClaimWorklistDetails worklist={worklist} />
									</List.Item>
								)}
							/>
						)}
					</Card>
				}
			>
				<Button
					type='link'
					icon={total}
					className={clsx(className, { 'font-sans underline': showUnderline, 'font-[inherit]': !showUnderline })}
					style={buttonStyle}
				/>
			</Tooltip>
		);
	}

	return 0;
};

export default ShowWorklists;
